import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Pencil, Trash2, Download, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const [fromDate, setFromDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [report, setReport] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editPaymentType, setEditPaymentType] = useState('');
  const [editDate, setEditDate] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [fromDate, toDate]);

  const fetchReport = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/transactions/report?from_date=${fromDate}&to_date=${toDate}`
      );
      setReport(data);
    } catch (error) {
      console.error('Error fetching report:', error);
    }
  };

  const exportCSV = () => {
    if (!report || !report.transactions.length) return;

    const headers = ['Date', 'Payment Type', 'Amount'];
    const rows = report.transactions.map(t => [
      t.date,
      t.payment_type === 'khata' ? 'Khata' : 'Cash',
      t.amount.toFixed(2)
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${fromDate}-to-${toDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setEditAmount(transaction.amount.toString());
    setEditPaymentType(transaction.payment_type);
    setEditDate(transaction.date);
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/transactions/${editingTransaction.id}`,
        {
          amount: parseFloat(editAmount),
          payment_type: editPaymentType,
          date: editDate
        }
      );
      setEditingTransaction(null);
      fetchReport();
    } catch (error) {
      console.error('Error updating transaction:', error);
    }
  };

  const handleDelete = async (transactionId) => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/transactions/${transactionId}`
      );
      setDeleteConfirm(null);
      fetchReport();
    } catch (error) {
      console.error('Error deleting transaction:', error);
    }
  };

  const setQuickRange = (days) => {
    setToDate(format(new Date(), 'yyyy-MM-dd'));
    setFromDate(format(subDays(new Date(), days), 'yyyy-MM-dd'));
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <div className="max-w-3xl mx-auto px-6 pt-8">
        {/* Date Range Selector */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="h-5 w-5 text-slate-500" />
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Date Range
            </h3>
          </div>
          
          {/* Quick Range Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setQuickRange(7)}
              className="px-3 py-1 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Last 7 Days
            </button>
            <button
              onClick={() => setQuickRange(30)}
              className="px-3 py-1 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Last 30 Days
            </button>
            <button
              onClick={() => setQuickRange(90)}
              className="px-3 py-1 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Last 90 Days
            </button>
          </div>

          {/* Custom Date Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="from-date-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="to-date-input"
              />
            </div>
          </div>

          {/* Export Button */}
          <button
            onClick={exportCSV}
            disabled={!report || !report.transactions.length}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all hover:-translate-y-[2px] hover:shadow-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="export-csv-button"
          >
            <Download className="h-5 w-5" />
            Export CSV
          </button>
        </div>

        {/* Summary Cards */}
        {report && (
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-white p-6 rounded-2xl border border-slate-200">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Total Khata
              </p>
              <p className="text-3xl font-bold text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="reports-total-khata">
                ₹{report.total_khata.toFixed(2)}
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Total Cash
              </p>
              <p className="text-3xl font-bold text-blue-600" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="reports-total-cash">
                ₹{report.total_cash.toFixed(2)}
              </p>
            </div>
          </div>
        )}

        {/* Transactions List */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            All Transactions ({report ? report.transactions.length : 0})
          </h3>

          {report && report.transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 bg-white rounded-2xl border border-slate-200">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/4fe567e4-fea0-4ee6-a821-9b513bd89b32/images/e014d0f488d5c3fffb336b783415d9d57b1293eb084de7c04fd0051550100a1c.png" 
                alt="No transactions" 
                className="w-48 h-48 opacity-50 mb-4"
              />
              <p className="text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>No transactions in this period</p>
            </div>
          ) : (
            report && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="reports-transactions-list">
                {report.transactions.map((transaction, index) => (
                  <div 
                    key={transaction.id} 
                    className={`flex justify-between items-center p-4 ${index !== report.transactions.length - 1 ? 'border-b border-slate-100' : ''}`}
                    data-testid={`report-transaction-item-${index}`}
                  >
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {transaction.payment_type === 'khata' ? 'Khata' : 'Cash'}
                      </p>
                      <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {format(new Date(transaction.date), 'MMM d, yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        ₹{transaction.amount.toFixed(2)}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(transaction)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          data-testid={`edit-transaction-button-${index}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(transaction)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          data-testid={`delete-transaction-button-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingTransaction} onOpenChange={() => setEditingTransaction(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Transaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Amount
              </label>
              <input
                type="number"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="edit-amount-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Payment Type
              </label>
              <select
                value={editPaymentType}
                onChange={(e) => setEditPaymentType(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="edit-payment-type-select"
              >
                <option value="khata">Khata</option>
                <option value="cash">Cash</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Date
              </label>
              <input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="edit-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setEditingTransaction(null)}
              variant="outline"
              className="rounded-xl"
              data-testid="edit-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              className="bg-blue-600 hover:bg-blue-700 rounded-xl"
              data-testid="edit-save-button"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Delete Transaction</DialogTitle>
          </DialogHeader>
          <p style={{ fontFamily: 'Manrope, sans-serif' }} className="text-slate-600">
            Are you sure you want to delete this transaction? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button
              onClick={() => setDeleteConfirm(null)}
              variant="outline"
              className="rounded-xl"
              data-testid="delete-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(deleteConfirm.id)}
              className="bg-red-600 hover:bg-red-700 rounded-xl"
              data-testid="delete-confirm-button"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;